import type { ToastObject } from '@base-ui/react/toast'
import type { ToastVariant } from './toast'
import { Close, Content, Description, Icon, Link, Root, Title } from './toast'
import { ToastPortal, ToastViewport } from './toast-viewport'
import { useToast } from './use-toast'

interface ToastData {
  type: ToastVariant
  description: string
  link?: {
    href: string
    text: string
  }
}

// Toast object from Base UI with our custom data merged at top level
interface ToastWithData {
  id: string
  type: ToastVariant
  description: string
  link?: {
    href: string
    text: string
  }
}

/**
 * ToastRegion - Renders all active toasts from the toast manager
 *
 * This component listens to the toast manager state and renders each active toast
 * with the appropriate variant styling and actions. It creates an ARIA live region
 * that announces toast notifications to screen readers.
 *
 * Usage:
 * ```tsx
 * <Toast.Provider>
 *   <App />
 *   <Toast.Region />
 * </Toast.Provider>
 * ```
 */
export function ToastRegion() {
  const toastManager = useToast()

  return (
    <ToastPortal>
      <ToastViewport>
        {toastManager.toasts.map((t: ToastObject<ToastData>) => {
          // Base UI merges our custom data directly onto the toast object
          // Cast to access the merged properties
          const toastData = t as unknown as ToastWithData
          const { type, description, link } = toastData

          if (!type || !description) return null

          return (
            <Root key={toastData.id} variant={type} toast={t}>
              <Icon variant={type} />
              <Content>
                <Title variant={type} />
                <Description>{description}</Description>
                {link && <Link href={link.href}>{link.text}</Link>}
              </Content>
              <Close
                variant={type}
                aria-label="Close"
                onClick={() => toastManager.close(toastData.id)}
              />
            </Root>
          )
        })}
      </ToastViewport>
    </ToastPortal>
  )
}
